import type { JSX } from 'react';
import { Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Nav } from '../sections/Nav/Nav';
import { Footer } from '../sections/Footer/Footer';
import { useLangSyncInit } from '../hooks/useLangSync';

/**
 * RootLayout — D-07 layout route.
 *
 * Renders <Nav/> <Outlet/> <Footer/> for every route. Mounts useLangSync once
 * (effectively once per app session) so language reconciliation runs on first
 * load and stays bound to the LangToggle for subsequent flips.
 *
 * Hoists hreflang + <title> into <head> via React 19 native metadata (D-18,
 * Phase 1 D-11). The runtime detects <link rel="alternate"> and <title>
 * elements rendered anywhere in the tree and moves them into <head>
 * automatically — no third-party metadata provider needed (Phase 1 dropped
 * that path; see 01-VERIFICATION.md note 1).
 *
 * VITE_SITE_URL is read at build time. Per D-19 / Phase 1 D-18 contract:
 * no fallback default in source (the env var must be set; build-time rebuild
 * is required when the value changes). WR-003: main.tsx validates the env
 * var at boot and throws if absent, so by the time this component renders
 * `siteUrl` is guaranteed to be a non-empty string.
 */
export function RootLayout(): JSX.Element {
  useLangSyncInit();
  const { t } = useTranslation();

  // D-19: VITE_SITE_URL is the source of truth. No default. The env var is
  // validated at boot in main.tsx (WR-003) so by here it is non-empty; the
  // type is `string | undefined` but the boot guard rules out the undefined
  // branch in practice. The non-null assertion encodes that runtime promise
  // for the type system.
  const siteUrl = import.meta.env.VITE_SITE_URL!;

  return (
    <>
      {/* React 19 native metadata hoisting — D-18 / Phase 1 D-11.
          These elements are auto-moved to <head> by the runtime. */}
      <title>{t('hreflang.title')}</title>
      <link rel="alternate" hrefLang="mk" href={`${siteUrl}/`} />
      <link rel="alternate" hrefLang="en" href={`${siteUrl}/?lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}/`} />

      <Nav />
      <Outlet />
      <Footer />
    </>
  );
}

export default RootLayout;
